<template>
  <div class="nx-content-selector">
    <v-autocomplete
      ref="selector"
      menu-props="closeOnContentClick"
      :disabled="disabled"
      :readonly="readonly"
      :items="formattedItems"
      :loading="loading"
      :persistent-hint="!!selectedContentHint"
      :hint="selectedContentHint"
      :value="selectedContentId"
      :error-messages="errorMessages"
      no-filter
      hide-selected
      chips
      item-path="title"
      item-text="title"
      item-value="id"
      v-bind="$attrs"
      @blur="onBlur"
      @focus="$emit('focus')"
      @input="acceptSelection"
      @update:searchInput="searchByText"
    >
      <template slot="append">
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              v-if="!readonly"
              icon
              @click.stop="openSearcher"
              v-on="on"
            >
              <v-icon>search</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('message.common.search') }}</span>
        </v-tooltip>
      </template>

      <template
        slot="selection"
        slot-scope="{ item, selected }"
      >
        <v-chip
          :selected="selected"
          :text="truncateTitle(item.title)"
          class="nx-chip"
        >
          <v-avatar>
            <v-icon class="nx-chip-icon">
              {{ item.icon }}
            </v-icon>
          </v-avatar>

          <span>{{ truncateTitle(item.title) }}</span>

          <template v-if="allowEditItems">
            <span
              v-if="openModalEditor"
              class="v-chip__close nx-chip--edit"
              @click.capture.stop="openModalEditorDialog = true"
            >
              <v-icon
                class="ml-2"
                :title="$t('message.common.edit')"
              >edit</v-icon>
            </span>
            <router-link
              v-else
              class="nx-chip--edit"
              :to="formatSelectorItemUrl(item)"
              target="_blank"
            >
              <span class="v-chip__close">
                <v-icon
                  class="ml-2"
                  :title="$t('message.common.edit')"
                >edit</v-icon>
              </span>
            </router-link>
          </template>

          <template v-if="!readonly">
            <span class="v-chip__close nx-chip--edit">
              <v-icon
                class="ml-2"
                :title="$t('message.common.delete')"
                @click.capture.stop="remove(item)"
              >cancel</v-icon>
            </span>
          </template>
        </v-chip>
      </template>

      <template
        slot="item"
        slot-scope="{ item }"
      >
        <v-list-tile-avatar>
          <v-icon>{{ item.icon }}</v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title
            v-html="$options.filters.highlight(item.title, searchText)"
          />
        </v-list-tile-content>
        <list-item-flag
          v-if="item.flag"
          v-bind="item.flag"
        />
      </template>

      <template slot="no-data">
        <li>
          <v-list-tile>
            <v-list-tile-avatar>
              <v-progress-circular
                v-if="loading"
                indeterminate
                :width="2"
              />
              <v-icon v-else-if="searchText">
                not_interested
              </v-icon>
              <v-icon v-else>
                edit
              </v-icon>
            </v-list-tile-avatar>

            <v-list-tile-content>
              <v-list-tile-title v-if="loading">
                {{ $t('message.common.loading') }}
              </v-list-tile-title>
              <v-list-tile-title v-else-if="searchText">
                {{ $t('message.search.noDataFound') }}
              </v-list-tile-title>
              <v-list-tile-title v-else>
                {{ $t('message.search.typeToSearch') }}
              </v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
        </li>
      </template>

      <template slot="append-outer">
        <nx-content-selector-wizard
          v-if="hasWizards"
          :wizards="wizards"
          :disabled="disabled"
          @entityCreated="entityCreated"
        />
        <slot name="append-outer" />
      </template>
    </v-autocomplete>

    <nx-searcher-dialog
      v-if="searcherModal"
      :active="searcherModalTimeout"
      :entity="searcherEntity"
      :entity-tabs="false"
      :filters="filters"
      :filters-params="additionalFiltersParams"
      :limit="1"
      :text="searchText"
      :value="searcherValue"
      title="Buscador"
      @dismiss="openSearcher(false)"
      @input="acceptSearcherValue"
    />

    <nx-entity-modal-editor-dialog
      v-if="openModalEditor"
      :active="openModalEditorDialog"
      :entity="selectedContent"
      :type="selectedContentType"
      :parent-title="getEntityModalTitle()"
      v-bind="selectedContent"
      @dismiss="dismissModalEditor"
    />

    <nx-dialog-confirm
      ref="confirm"
      :title="$t('message.content.selector.confirmTitle')"
      :text="$t('message.content.selector.confirmText')"
    />
  </div>
</template>


<script>
import AllowedContentsOnly from '@/modules/searcher/mixins/allowed-contents-only';
import api from '@/modules/searcher/api';
import Loadable from '@/mixins/loadable';
import Searchable from '@/modules/searcher/mixins/searchable';
import {
  isObject, toNumber, isEmpty, get,
} from 'lodash';
import { delay, toArray } from '@/utils';
import { typeToIcon } from '@/modules/content/config';
import { mapGetters } from 'vuex';
import searcherConfig from '@/modules/searcher/config';
import NxSearcherDialog from '@/modules/searcher/SearcherDialog';
import NxDialogConfirm from '@/components/dialogs/Confirm';
import isArray from 'lodash/isArray';
import NxContentSelectorWizard from '@/modules/content/selector/components/ContentSelectorWizard';
import ListItemFlag from '@/components/list/components/ListItemFlag';

/**
 * Content selector module.
 *
 * @property {[]} [type] The content type that can be selected. They will added as a filter
 *                               in when searching for contents.
 * @property {string} [identifier] String to use as identifier for this module.
 * @property {[]} [value] Array of initial selected contents.
 */
export default {
  name: 'NxContentSelector',
  components: {
    ListItemFlag,
    NxContentSelectorWizard,
    NxDialogConfirm,
    NxSearcherDialog,
    NxEntityModalEditorDialog: () => import('@/modules/entity/editor/EntityModalEditorDialog'),
  },
  mixins: [Searchable, Loadable, AllowedContentsOnly],
  inject: {
    $getParentEditor: { default: null },
  },
  props: {
    additionalFilters: {
      type: Object,
      default: () => ({}),
    },
    additionalFiltersParams: {
      type: Object,
      default: () => ({}),
    },
    type: {
      type: [String, Array],
    },
    value: {
      type: [Object, String, Number],
    },
    siteId: {
      type: [Number],
    },
    disabled: {
      type: Boolean,
    },
    titleLength: {
      type: [Number],
      default: 0,
    },
    allowEditItems: {
      type: Boolean,
      default: () => true,
    },
    readonly: {
      type: Boolean,
      default: () => false,
    },
    excludedContentIds: {
      type: Array,
      default: () => ([]),
    },
    errorMessages: {
      type: Array,
      default: () => ([]),
    },
    openModalEditor: {
      type: Boolean,
    },
    openEditorAfterWizardCreate: {
      type: Boolean,
    },
    wizard: {
      type: [String, Array],
    },
    searcherEntity: {
      type: String,
      default: 'content',
    },
  },
  data() {
    return {
      filters: {
        siteId: this.siteId || this.getDefaultSiteId(),
      },
      selectedContent: null,
      searcherModal: false,
      searcherModalTimeout: false,
      searchOnFiltersChange: false,
      openModalEditorDialog: false,
    };
  },
  computed: {
    ...mapGetters(['features']),
    entity() {
      return this.searcherEntity;
    },
    selectedContentId() {
      if (isObject(this.selectedContent)) {
        return this.selectedContent.id;
      }
      return this.selectedContent;
    },
    selectedContentType() {
      if (isObject(this.selectedContent)) {
        return this.selectedContent.type;
      }
      return null;
    },
    selectedContentHint() {
      if (!this.selectedContent
        || !this.features.SHOW_ENTITY_IDS_IN_SELECTORS) {
        return null;
      }

      const contentSelectorHintConfig = searcherConfig.contentSelectorHintConfig;
      if (!Array.isArray(contentSelectorHintConfig)
        || contentSelectorHintConfig.length === 0
      ) {
        return null;
      }

      return contentSelectorHintConfig.map(f => f(this.selectedContent))
        .filter(result => result)
        .join(' | ');
    },
    formattedItems() {
      const items = this.items
        .map(item => Object.assign({}, item.entity))
        .map(this.formatSelectorItem);
      if (this.selectedContent) {
        items.push(this.formatSelectorItem(this.selectedContent));
      }
      return items;
    },
    searcherValue() {
      return toArray(this.selectedContent);
    },
    hasWizards() {
      if (isArray(this.wizard)) {
        return this.wizard.length > 0;
      }
      return !!this.wizard;
    },
    wizards() {
      return toArray(this.wizard);
    },
  },
  watch: {
    siteId: {
      handler() {
        this.filters.siteId = this.getDefaultSiteId();
      },
      immediate: true,
    },
    additionalFilters(additionalFilters = {}) {
      const newFilters = {
        siteId: this.getDefaultSiteId(),
        ...additionalFilters,
      };

      if (!isEmpty(this.filters.excludedIds)) {
        newFilters.excludedIds = this.filters.excludedIds;
      }

      if (this.filters.text) {
        newFilters.text = this.filters.text;
      }

      this.filters = newFilters;
    },
    excludedContentIds(val) {
      if (isEmpty(val)) {
        return;
      }
      this.filters = { ...this.filters, excludedIds: val, ...this.additionalFilters };
    },
    type(value) {
      this.updateTypeFilter(value);
    },
    value: {
      async handler(value) {
        // If a null or undefined value is given, remove the selected content.
        if (!value) {
          this.selectedContent = null;
          return;
        }

        // If a card is given, and it is not the current selected content, replace it.
        const currentContentId = this.selectedContent && this.selectedContent.id;
        if (isObject(value)
          && currentContentId !== value.id) {
          this.selectedContent = value;
          return;
        }

        // If a contentId is given, and it is not the current selected content's id, fetch its card
        // and replace it.
        const newContentId = toNumber(value);
        if (!isNaN(newContentId)
          && currentContentId !== newContentId) {
          this.loadingStart();
          this.selectedContent = await api.fetch('content', newContentId);
          this.loadingFinish();
        }
      },
      immediate: true,
    },
    searchText(value) {
      if (value) {
        this.$set(this.filters, 'text', value);
      } else {
        this.$delete(this.filters, 'text');
      }
    },
  },
  created() {
    this.updateTypeFilter(this.type);
  },
  mounted() {
    if (isEmpty(this.excludedContentIds)) {
      this.filters = { ...this.filters, ...this.additionalFilters };
    } else {
      this.filters = { ...this.filters, excludedIds: this.excludedContentIds, ...this.additionalFilters };
    }
  },
  methods: {
    getDefaultSiteId() {
      if (this.siteId) {
        return this.siteId;
      }
      return this.$store.getters.siteId || null;
    },
    getEntityModalTitle() {
      if (!this.$getParentEditor || !this.openModalEditor) {
        return null;
      }
      return this.$getParentEditor().description || this.$getParentEditor().title;
    },
    acceptSelection(selectedItem) {
      this.selectedContent = (this.items.find(item => item.entityId === selectedItem) || {}).entity;
      this.$emit('input', this.selectedContent);
    },
    acceptSearcherValue(value) {
      let content = value.length > 0 ? value[0] : null;
      content = this.filterPublishedContents(content, this.$t('message.content.selector.nonPublishedContentSelectedWarning'));
      content = this.filterContentsByType(content, this.$t('message.content.selector.nonAllowedContentTypeSelectedWarning'));
      if (content === false) {
        return;
      }

      this.selectedContent = content;
      this.$emit('input', this.selectedContent);
      // Workaround for validation
      // If blur is not present validation is not triggered if user interacted with searcher filters:
      // @see: https://mgr.bitban.com/issues/90483
      this.$emit('blur', this.selectedContent);
    },
    async entityCreated(entity) {
      this.$emit('input', entity);
      await delay(100);
      this.openModalEditorDialog = true;
    },
    onBlur() {
      this.$emit('blur', this.selectedContent);
    },
    async remove() {
      const confirm = await this.$refs.confirm.open();
      if (confirm) {
        this.selectedContent = null;
        this.$emit('blur', this.selectedContent);
        this.$emit('input', this.selectedContent);
      }
    },
    openSearcher(value = true) {
      value = !!value;
      this.searcherModal = value;
      setTimeout(() => {
        this.searcherModalTimeout = value;
      }, 300);
    },
    formatSelectorItemUrl(item) {
      const route = {
        name: 'content.editor',
        params: {
          id: item.id,
          type: item.type,
        },
      };
      if (typeof item.siteAlias !== 'undefined') {
        route.params.site = item.siteAlias;
      }
      return route;
    },
    /**
     * Maps a content to a ContentSearchItem.
     * @param {ContentCard} content
     * @returns {ContentSelectorItem}
     */
    formatSelectorItem(content) {
      /**
       * @typedef {Object} ContentSelectorItem
       * @property {number} id
       * @property {string} icon
       * @property {string} title
       */
      return {
        id: content.id || content,
        title: content.title || content,
        icon: typeToIcon(content.type),
        type: content.type,
        siteAlias: content.siteAlias,
        flag: this.features.SHOW_PUBLISH_FLAG_SEARCH_CONTENT ? get(content, 'list.flag') : null,
      };
    },
    truncateTitle(text) {
      let titleLength = this.titleLength;

      // Set titleLength if not specified
      if (!titleLength && this.$refs.selector) {
        titleLength = Math.min(25, (this.$refs.selector.$el.clientWidth * 30) / 400);
      }

      if (text && titleLength > 0) {
        return text.substr(0, titleLength) + ((text.length > titleLength) ? '...' : '');
      }
      return text;
    },
    dismissModalEditor() {
      this.openModalEditorDialog = false;
      this.$emit('input', this.selectedContent);
    },
  },
};
</script>

<style scoped lang="stylus">
.nx-chip--edit {
  pointer-events: all;
  text-decoration: none;
  color: inherit;
  display: contents;
}

.nx-search-btn {
  margin-top: 10px;
}
</style>
